import { z } from 'zod';
import type { ConfigTahiti } from '../config';
import { analyser } from './analyse';
import type { AppelHttp } from './http';

/**
 * The generic H2Data calls the Tahiti calls are built on: a filtered read and an
 * insertion. Each Tahiti call (`documents.ts`, `taches.ts`, `resultats.ts`) brings the
 * Zod schema of the rows it asks for; the envelope is validated here.
 *
 * Shapes taken from the reference script of the team (Rosetta v5, confirmed working): a
 * read is `POST /api/v2/request` with `{ table, fields, where, limit, lang }` and answers
 * `{ data: [rows] }`; an insertion is `POST /api/v2/insert` with `{ table, data: [record] }`
 * and answers an object that carries the identifier of the created record.
 *
 * TO VALIDATE(#6): `orderBy` (the script never sorts: the client does not rely on it) and
 * the operator `LIKE` of the filter come from the Tahiti MCP, not from the script.
 */

/** An identifier, as H2Data writes it (a numeric string, sometimes a number): a string. */
export const idSchema = z.union([z.string().min(1), z.number().int()]).transform(String);

/** A filter, `[field, value, operator]`. One clause per read: the shape of several is unknown. */
export const filtreSchema = z.tuple([z.string(), z.union([z.string(), z.number()]), z.string()]);
export type Filtre = z.infer<typeof filtreSchema>;

/** Language of the multilingual fields the client reads and writes: French. */
export const CODE_LANGUE = 'fr';

export const requeteLectureSchema = z.object({
  table: z.string().min(1),
  fields: z.array(z.string().min(1)).min(1),
  where: filtreSchema.optional(),
  orderBy: z.array(z.object({ field: z.string(), direction: z.enum(['ASC', 'DESC']) })).optional(),
  limit: z.number().int().positive().optional(),
  /** Language in which the multilingual fields come back (as a plain text). */
  lang: z.string().min(1).optional(),
});
export type RequeteLecture = z.infer<typeof requeteLectureSchema>;

const enveloppeSchema = z.object({
  success: z.boolean().optional(),
  data: z.array(z.unknown()),
});

export const requeteInsertionSchema = z.object({
  table: z.string().min(1),
  data: z.array(z.record(z.string(), z.unknown())).min(1),
});
export type RequeteInsertion = z.infer<typeof requeteInsertionSchema>;

/**
 * The identifier of what was created. The reference script looks for it in `data.id`,
 * `data[0].id`, `id`, `taskId` and `task.id`, in that order: so does the client.
 */
export const reponseCreationSchema = z
  .object({
    data: z.union([z.object({ id: idSchema }), z.array(z.object({ id: idSchema })).min(1)]),
  })
  .or(z.object({ id: idSchema }))
  .or(z.object({ taskId: idSchema }))
  .or(z.object({ task: z.object({ id: idSchema }) }))
  .transform((reponse) => {
    if ('data' in reponse)
      return Array.isArray(reponse.data) ? reponse.data[0]?.id : reponse.data.id;
    if ('id' in reponse) return reponse.id;
    if ('taskId' in reponse) return reponse.taskId;
    return reponse.task.id;
  })
  .pipe(z.string().min(1));

/**
 * A text field that H2Data may write as a plain string (read with `lang`) or as the
 * multilingual form `{ <lang>: { ai, value } }`: the plain text, in French when there is one.
 */
export const texteLocaliseSchema = z.union([
  z.string(),
  z
    .record(z.string(), z.object({ value: z.string().nullish() }).passthrough())
    .transform(
      (parLangue) =>
        parLangue[CODE_LANGUE]?.value ??
        Object.values(parLangue).find((entree) => typeof entree.value === 'string')?.value ??
        null,
    ),
]);

/** A multilingual text to write: the same value in each of the languages. */
export function texteMultilingue(texte: string, langues: readonly string[]) {
  const valeur = { ai: false, value: texte };
  return Object.fromEntries(langues.map((langue) => [langue, valeur]));
}

/**
 * TO VALIDATE(#4): the shape of `created_at`. The live data shows `2026-09-22
 * 11:50:18.060236`: no time zone and microseconds. It is read as UTC, because the live
 * data does not say which zone Tahiti stores; if it turns out to be the local time of the
 * server, the hour shown on a card is off by the offset and the fix is HERE (and in the
 * doubles).
 */
const FORME_DATE = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:?\d{2})?$/;

/** An ISO 8601 instant with the `Z` of UTC, or `null` for a value that is not a date. */
export function dateTahitiVersIso(valeur: string): string | null {
  const morceaux = FORME_DATE.exec(valeur.trim());
  if (morceaux === null) return null;
  const [, jour, heure, fraction = '', zone = 'Z'] = morceaux;
  const decalage = /^[+-]\d{4}$/.test(zone) ? `${zone.slice(0, 3)}:${zone.slice(3)}` : zone;
  const instant = Date.parse(`${jour}T${heure}.${`${fraction}000`.slice(0, 3)}${decalage}`);
  return Number.isNaN(instant) ? null : new Date(instant).toISOString();
}

export const dateTahitiSchema = z.string().transform((valeur, contexte) => {
  const iso = dateTahitiVersIso(valeur);
  if (iso === null) {
    contexte.addIssue({ code: z.ZodIssueCode.custom, message: 'not a date' });
    return z.NEVER;
  }
  return iso;
});

/**
 * Reads the rows of a table and validates each of them with `ligne`. `surRefus` says what a
 * refusal (400, 401, 403) means for this read, when it is not "the session expired": the
 * reading of the steps of a translation (#7) takes a refusal for "not allowed", not for a
 * session to renew.
 */
export async function lireLignes<T>(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  requete: RequeteLecture,
  ligne: z.ZodType<T, unknown>,
  surRefus?: (statut: number) => Error,
): Promise<T[]> {
  const corps = requeteLectureSchema.parse(requete);
  const reponse = await appel({
    methode: 'POST',
    url: `${config.urlH2data}/api/v2/request`,
    jeton,
    corps,
    ...(surRefus === undefined ? {} : { surRefus }),
  });
  return analyser(enveloppeSchema, reponse, `read of ${requete.table}`).data.map((brute) =>
    analyser(ligne, brute, `row of ${requete.table}`),
  );
}

/** Inserts a record and returns its identifier. */
export async function inserer(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  requete: { table: string; data: Record<string, unknown> },
): Promise<string> {
  const corps = requeteInsertionSchema.parse({ table: requete.table, data: [requete.data] });
  const reponse = await appel({
    methode: 'POST',
    url: `${config.urlH2data}/api/v2/insert`,
    jeton,
    corps,
  });
  return analyser(reponseCreationSchema, reponse, `insertion in ${requete.table}`);
}
